import { useState } from "react";
import { X, Cpu, MemoryStick, HardDrive } from "lucide-react";
import { ServiceTierSelector } from "@/components/vm/ServiceTierSelector";
import { PricingSummaryPanel } from "@/components/vm/PricingSummaryPanel";
import { LoadingSpinner } from "@/components/common/LoadingSpinner";
import { usePricingRule, useResizeVM } from "@/hooks/useBilling";
import type { VM, ServiceTier } from "@/types";
import type { BackupPlan, VMResizePayload } from "@/types/billing";

interface VMResizeModalProps {
  vm: VM;
  onClose: () => void;
}

const BACKUP_OPTIONS: { value: BackupPlan; label: string }[] = [
  { value: "none", label: "No Backup" },
  { value: "snapshot", label: "Snapshot" },
  { value: "full", label: "Full Backup" },
  { value: "incremental", label: "Incremental" },
];

export function VMResizeModal({ vm, onClose }: VMResizeModalProps) {
  const [cores, setCores] = useState(vm.cores);
  const [ramMb, setRamMb] = useState(vm.memory_mb);
  const [diskGb, setDiskGb] = useState(vm.disk_gb);
  const [tier, setTier] = useState<ServiceTier>(vm.service_tier as ServiceTier);
  const [backupPlan, setBackupPlan] = useState<BackupPlan>("none");

  const { data: pricingRule } = usePricingRule();
  const resizeVM = useResizeVM();

  const hasChanges =
    cores !== vm.cores ||
    ramMb !== vm.memory_mb ||
    diskGb !== vm.disk_gb ||
    tier !== (vm.service_tier as ServiceTier);

  const handleSubmit = () => {
    const payload: Partial<VMResizePayload> = {};
    if (cores !== vm.cores) payload.cores = cores;
    if (ramMb !== vm.memory_mb) payload.memory_mb = ramMb;
    if (diskGb !== vm.disk_gb) payload.disk_gb = diskGb;
    if (tier !== (vm.service_tier as ServiceTier)) payload.service_tier = tier;
    payload.backup_plan = backupPlan;

    resizeVM.mutate(
      { vmId: vm.id, payload },
      { onSuccess: () => onClose() }
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl border border-gray-800 bg-gray-950 p-8">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute right-4 top-4 rounded-lg p-1 hover:bg-gray-800"
        >
          <X className="h-5 w-5 text-gray-400" />
        </button>

        <h2 className="text-xl font-bold text-gray-100 mb-6">
          Resize VM: {vm.name}
        </h2>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          {/* Left: Controls */}
          <div className="space-y-6 lg:col-span-2">
            {/* Topology */}
            <section>
              <h3 className="mb-3 text-sm font-semibold text-gray-300">
                Topology
              </h3>
              <ServiceTierSelector value={tier} onChange={setTier} />
            </section>

            {/* Resources */}
            <section className="space-y-5 rounded-xl border border-gray-800 bg-gray-900/50 p-5">
              {/* CPU */}
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-300">
                    <Cpu className="h-4 w-4 text-blue-400" />
                    CPU Cores
                  </label>
                  <span className="rounded bg-gray-800 px-2 py-0.5 text-sm font-mono text-gray-200">
                    {cores}
                    {cores !== vm.cores && (
                      <span className="ml-1 text-xs text-amber-400">
                        (was {vm.cores})
                      </span>
                    )}
                  </span>
                </div>
                <input
                  type="range" min={1} max={16} step={1}
                  value={cores}
                  onChange={(e) => setCores(Number(e.target.value))}
                  className="w-full accent-blue-500"
                />
              </div>

              {/* RAM */}
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-300">
                    <MemoryStick className="h-4 w-4 text-purple-400" />
                    RAM
                  </label>
                  <span className="rounded bg-gray-800 px-2 py-0.5 text-sm font-mono text-gray-200">
                    {ramMb >= 1024 ? `${(ramMb / 1024).toFixed(1)} GB` : `${ramMb} MB`}
                    {ramMb !== vm.memory_mb && (
                      <span className="ml-1 text-xs text-amber-400">
                        (was {vm.memory_mb >= 1024 ? `${(vm.memory_mb / 1024).toFixed(1)} GB` : `${vm.memory_mb} MB`})
                      </span>
                    )}
                  </span>
                </div>
                <input
                  type="range" min={512} max={32768} step={256}
                  value={ramMb}
                  onChange={(e) => setRamMb(Number(e.target.value))}
                  className="w-full accent-purple-500"
                />
              </div>

              {/* Disk (can only grow) */}
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-300">
                    <HardDrive className="h-4 w-4 text-green-400" />
                    Disk (GB)
                  </label>
                  <span className="rounded bg-gray-800 px-2 py-0.5 text-sm font-mono text-gray-200">
                    {diskGb} GB
                    {diskGb !== vm.disk_gb && (
                      <span className="ml-1 text-xs text-amber-400">
                        (was {vm.disk_gb} GB)
                      </span>
                    )}
                  </span>
                </div>
                <input
                  type="range"
                  min={vm.disk_gb} max={500} step={5}
                  value={diskGb}
                  onChange={(e) => setDiskGb(Number(e.target.value))}
                  className="w-full accent-green-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Disk can only be increased (min: {vm.disk_gb} GB)
                </p>
              </div>
            </section>

            {/* Backup plan */}
            <section>
              <h3 className="mb-3 text-sm font-semibold text-gray-300">
                Backup Plan
              </h3>
              <div className="flex flex-wrap gap-2">
                {BACKUP_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setBackupPlan(opt.value)}
                    className={`rounded-lg border px-4 py-2 text-sm transition-all ${
                      backupPlan === opt.value
                        ? "border-blue-500 bg-blue-500/10 text-blue-400"
                        : "border-gray-700 text-gray-400 hover:border-gray-600"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </section>

            {/* Actions */}
            <div className="flex items-center gap-4">
              <button
                onClick={handleSubmit}
                disabled={!hasChanges || resizeVM.isPending}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {resizeVM.isPending ? (
                  <LoadingSpinner size="sm" />
                ) : null}
                {resizeVM.isPending ? "Applying..." : "Apply Changes"}
              </button>
              <button
                onClick={onClose}
                className="rounded-lg border border-gray-700 px-6 py-2.5 text-sm text-gray-400 hover:bg-gray-800"
              >
                Cancel
              </button>
            </div>

            {resizeVM.isError && (
              <p className="text-sm text-red-400">
                Failed to resize: {resizeVM.error.message}
              </p>
            )}
          </div>

          {/* Right: Pricing comparison */}
          <div className="lg:sticky lg:top-6 lg:self-start">
            <PricingSummaryPanel
              pricingRule={pricingRule ?? null}
              cores={cores}
              ramMb={ramMb}
              diskGb={diskGb}
              tier={tier}
              backupPlan={backupPlan}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
